import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Chart, registerables } from 'chart.js';
import {
  RefreshCw, AlertCircle, Save, Droplet, Trash2,
  Activity, Thermometer, Droplets, Waves, ToggleLeft,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import PageHeader from '../../components/PageHeader';
import sensorDevicesService, {
  type SensorDevice,
  type SensorReading,
  type SensorChannel,
  type TankStats,
} from '../../services/sensorDevices';
import sensorRulesService, { type SensorRule } from '../../services/sensorRules';
import orchardService from '../../services/orchard';
import TankVolumeCard from '../../components/irrigation/TankVolumeCard';
import SensorRuleList from '../../components/irrigation/SensorRuleList';
import SensorRuleForm from '../../components/irrigation/SensorRuleForm';
import SensorRuleEventsModal from '../../components/irrigation/SensorRuleEventsModal';
import SensorRuleRelayActivity from '../../components/irrigation/SensorRuleRelayActivity';
import { useAppRefresh } from '../../hooks/useAppRefresh';
import { isOrgSuperAdmin } from '../../utils/auth';
import '../../styles/GlobalPageStyles.css';
import React from 'react';

Chart.register(...registerables);

const CHANNEL_COLORS = [
  '#2563eb', '#dc2626', '#16a34a', '#9333ea',
  '#ea580c', '#0891b2', '#d97706', '#4f46e5',
];

const CHANNEL_ICONS: Record<string, React.ReactNode> = {
  temperature: <Thermometer className="w-4 h-4 text-orange-500" />,
  humidity: <Droplets className="w-4 h-4 text-sky-500" />,
  water_level: <Waves className="w-4 h-4 text-blue-500" />,
  tank_level: <Droplets className="w-4 h-4 text-blue-600" />,
  digital_input: <ToggleLeft className="w-4 h-4 text-gray-500" />,
  generic_adc: <Activity className="w-4 h-4 text-violet-500" />,
};

export default function SensorDeviceDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const isSuperAdmin = isOrgSuperAdmin(user);
  const [device, setDevice] = useState<SensorDevice | null>(null);
  const [channels, setChannels] = useState<SensorChannel[]>([]);
  const [readings, setReadings] = useState<SensorReading[]>([]);
  const [selectedChannel, setSelectedChannel] = useState<{ kind: string; index: number } | null>(null);
  const [tankStats, setTankStats] = useState<TankStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingReadings, setDeletingReadings] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [configDraft, setConfigDraft] = useState('');
  const [farms, setFarms] = useState<Array<{ id: number; name: string }>>([]);
  const [farmIdDraft, setFarmIdDraft] = useState<number | null>(null);
  const [savingFarm, setSavingFarm] = useState(false);
  const [rules, setRules] = useState<SensorRule[]>([]);
  const [ruleFormOpen, setRuleFormOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<SensorRule | null>(null);
  const [historyRule, setHistoryRule] = useState<SensorRule | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);

  const isMultiChannel = channels.length > 0;

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [d, chs, farmsRes] = await Promise.all([
        sensorDevicesService.getDevice(id),
        sensorDevicesService.getChannels(id).catch(() => [] as SensorChannel[]),
        orchardService.getFarms(1, 100).catch(() => null),
      ]);
      setDevice(d);
      setNameDraft(d.name || '');
      setConfigDraft(JSON.stringify(d.config || {}, null, 2));
      setChannels(chs);
      setFarmIdDraft(d.farm_id ?? null);
      const farmList = farmsRes && farmsRes.success ? (farmsRes.data?.farms ?? []) : [];
      setFarms(farmList.map((f: { id: number; name: string }) => ({ id: f.id, name: f.name })));

      // Load readings — if multi-channel, load the first channel by default
      if (chs.length > 0) {
        const first = chs[0];
        setSelectedChannel({ kind: first.channel_kind, index: first.channel_index });
        const r = await sensorDevicesService.getReadings(id, {
          limit: 500,
          channelIndex: first.channel_index,
          channelKind: first.channel_kind,
        });
        setReadings(r);
      } else {
        const r = await sensorDevicesService.getReadings(id, { limit: 500 });
        setReadings(r);
      }
    } catch {
      setError('Failed to load device');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useAppRefresh(useCallback(() => { load(); }, [load]));

  async function loadChannelReadings(kind: string, index: number) {
    if (!id) return;
    setSelectedChannel({ kind, index });
    setRuleFormOpen(false);
    setEditingRule(null);
    try {
      const r = await sensorDevicesService.getReadings(id, {
        limit: 500,
        channelIndex: index,
        channelKind: kind,
      });
      setReadings(r);
    } catch {
      setError('Failed to load readings');
    }
  }

  // Load rules for the selected channel
  useEffect(() => {
    if (!id || !selectedChannel) { setRules([]); return; }
    let active = true;
    sensorRulesService.listRules({
      device_id: id,
      channel_index: selectedChannel.index,
      channel_kind: selectedChannel.kind,
    })
      .then(rs => { if (active) setRules(rs); })
      .catch(() => { if (active) setRules([]); });
    return () => { active = false; };
  }, [id, selectedChannel]);

  async function reloadRules() {
    if (!id || !selectedChannel) return;
    try {
      const rs = await sensorRulesService.listRules({
        device_id: id,
        channel_index: selectedChannel.index,
        channel_kind: selectedChannel.kind,
      });
      setRules(rs);
    } catch {
      // non-fatal
    }
  }

  async function handleToggleRule(rule: SensorRule, next: boolean) {
    try {
      await sensorRulesService.updateRule(rule.id, { enabled: next });
      await reloadRules();
    } catch {
      setError('Failed to update rule');
    }
  }

  async function handleDeleteRule(rule: SensorRule) {
    if (!window.confirm(`Delete rule "${rule.name}"?`)) return;
    try {
      await sensorRulesService.deleteRule(rule.id);
      await reloadRules();
    } catch {
      setError('Failed to delete rule');
    }
  }

  // Fetch tank stats when active channel has tank config
  useEffect(() => {
    if (!id || !selectedChannel) { setTankStats(null); return; }
    const ch = channels.find(
      c => c.channel_kind === selectedChannel.kind && c.channel_index === selectedChannel.index
    );
    const tankCfg = ch?.config && (ch.config as Record<string, unknown>).tank;
    if (!ch || ch.type_key !== 'water_level' || !tankCfg) {
      setTankStats(null);
      return;
    }
    sensorDevicesService
      .getTankStats(id, ch.channel_kind, ch.channel_index, 14)
      .then(setTankStats)
      .catch(() => setTankStats(null));
  }, [id, selectedChannel, channels]);

  // Render chart
  useEffect(() => {
    if (!canvasRef.current || readings.length === 0) return;

    const sorted = [...readings].reverse();
    const labels = sorted.map(r => new Date(r.reading_time).toLocaleString());
    const data = sorted.map(r => (r.value === null || r.value === undefined ? null : Number(r.value)));

    const activeChannel = isMultiChannel && selectedChannel
      ? channels.find(c => c.channel_kind === selectedChannel.kind && c.channel_index === selectedChannel.index)
      : null;

    const label = activeChannel
      ? `${activeChannel.name || activeChannel.type_display_name || 'Value'}${activeChannel.type_unit ? ' (' + activeChannel.type_unit + ')' : ''}`
      : `${device?.type_display_name || 'Value'}${device?.type_unit ? ' (' + device.type_unit + ')' : ''}`;

    const colorIndex = activeChannel
      ? channels.indexOf(activeChannel) % CHANNEL_COLORS.length
      : 0;
    const color = CHANNEL_COLORS[colorIndex];

    if (chartRef.current) {
      chartRef.current.data.labels = labels;
      chartRef.current.data.datasets[0].data = data;
      chartRef.current.data.datasets[0].label = label;
      chartRef.current.data.datasets[0].borderColor = color;
      chartRef.current.data.datasets[0].backgroundColor = color + '20';
      chartRef.current.update();
      return;
    }

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    chartRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels,
        datasets: [{
          label,
          data,
          borderColor: color,
          backgroundColor: color + '20',
          fill: true,
          tension: 0.2,
          pointRadius: 0,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: { x: { display: false } },
        plugins: { legend: { display: isMultiChannel } },
      },
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readings, device, selectedChannel]);

  async function handleSave() {
    if (!device) return;
    setSaving(true);
    setError(null);
    try {
      let config: Record<string, unknown> | null = null;
      if (configDraft.trim()) {
        try {
          config = JSON.parse(configDraft);
        } catch {
          setError('Config is not valid JSON');
          setSaving(false);
          return;
        }
      }
      const updated = await sensorDevicesService.updateDevice(device.device_id, {
        name: nameDraft || null,
        config,
      });
      setDevice(updated);
    } catch {
      setError('Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function handleFarmChange(newFarmId: number | null) {
    if (!device) return;
    setSavingFarm(true);
    setError(null);
    try {
      const updated = await sensorDevicesService.updateDevice(device.device_id, { farm_id: newFarmId });
      setDevice(updated);
      setFarmIdDraft(updated.farm_id ?? null);
    } catch {
      setError('Failed to update farm assignment');
    } finally {
      setSavingFarm(false);
    }
  }

  async function handleDeleteAllReadings() {
    if (!device) return;
    const label = device.name || device.device_id;
    if (!window.confirm(
      `Permanently delete ALL readings for "${label}"?\n\n` +
      `This cannot be undone. Channel configs and the device itself are kept.`
    )) return;
    setDeletingReadings(true);
    setError(null);
    try {
      const result = await sensorDevicesService.deleteAllReadings(device.device_id);
      setReadings([]);
      setTankStats(null);
      alert(`Deleted ${result.deleted.toLocaleString()} reading(s).`);
    } catch {
      setError('Failed to delete readings');
    } finally {
      setDeletingReadings(false);
    }
  }

  if (!device && loading) {
    return (
      <>
        <PageHeader title="Sensor Device" icon={<Droplet className="w-5 h-5 text-blue-600" />} />
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div>
            <div className="text-sm text-gray-400 flex items-center gap-2">
              <RefreshCw className="w-4 h-4 animate-spin" /> Loading…
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!device) {
    return (
      <>
        <PageHeader title="Sensor Device" icon={<Droplet className="w-5 h-5 text-blue-600" />} />
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div>
            <div className="text-sm text-red-600">Device not found.</div>
          </div>
        </div>
      </>
    );
  }

  const latest = readings[0];
  const activeChannel = isMultiChannel && selectedChannel
    ? channels.find(c => c.channel_kind === selectedChannel.kind && c.channel_index === selectedChannel.index)
    : null;
  const displayUnit = activeChannel?.type_unit || device.type_unit || '';

  return (
    <>
      <PageHeader
        title={device.name || device.device_id}
        subtitle={device.type_display_name || device.type_key}
        icon={<Droplet className="w-5 h-5 text-blue-600" />}
        actionButtons={
          isSuperAdmin ? (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDeleteAllReadings}
                disabled={deletingReadings || readings.length === 0}
                title={readings.length === 0 ? 'No readings to delete' : 'Permanently delete all readings for this device'}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                aria-label="Delete all readings"
              >
                <Trash2 className={`w-4 h-4 sm:mr-1 ${deletingReadings ? 'animate-pulse' : ''}`} />
                <span className="hidden sm:inline">
                  {deletingReadings ? 'Deleting…' : 'Delete all readings'}
                </span>
              </Button>
            </div>
          ) : undefined
        }
      />
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="space-y-6">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
              <button onClick={() => setError(null)} className="ml-auto text-red-400">×</button>
            </div>
          )}

          {/* Channel selector for multi-channel devices */}
          {isMultiChannel && (
            <div className="flex gap-2 flex-wrap">
              {channels.map((ch) => {
                const isActive = selectedChannel?.kind === ch.channel_kind && selectedChannel?.index === ch.channel_index;
                return (
                  <button
                    key={`${ch.channel_kind}-${ch.channel_index}`}
                    onClick={() => loadChannelReadings(ch.channel_kind, ch.channel_index)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  >
                    {CHANNEL_ICONS[ch.type_key] || <Activity className="w-4 h-4" />}
                    {ch.name || `${ch.channel_kind === 'digital' ? 'DI' : 'ADC'} ${ch.channel_index}`}
                  </button>
                );
              })}
            </div>
          )}

          {tankStats ? (
            <TankVolumeCard
              stats={tankStats}
              title={activeChannel?.name ? `${activeChannel.name} — tank volume` : 'Tank volume'}
              depthMm={latest?.value ?? null}
              readingTime={latest?.reading_time ?? null}
              online={device.online}
            />
          ) : (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">
                  {activeChannel ? (activeChannel.name || activeChannel.type_display_name || 'Latest') : 'Latest'}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex items-baseline gap-4">
                  <div className="text-4xl font-bold text-gray-800">
                    {latest && latest.value !== null
                      ? `${Number(latest.value).toFixed(1)}${displayUnit ? ' ' + displayUnit : ''}`
                      : '—'}
                  </div>
                  <div className="text-sm text-gray-500">
                    {latest ? new Date(latest.reading_time).toLocaleString() : 'No readings yet'}
                  </div>
                  <div className="ml-auto text-xs">
                    <span className={`inline-block px-2 py-0.5 rounded-full ${
                    device.online ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                  }`}>
                      {device.online ? 'Online' : 'Offline'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">History ({readings.length} readings)</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="h-64">
                {readings.length === 0 ? (
                  <div className="text-sm text-gray-400 italic">No readings yet.</div>
                ) : (
                  <canvas ref={canvasRef} />
                )}
              </div>
            </CardContent>
          </Card>

          {selectedChannel && activeChannel && (
            <Card>
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-base">
                  Automation rules
                  <span className="ml-2 text-xs text-gray-500 font-normal">
                    for {activeChannel.name || `${activeChannel.channel_kind} ${activeChannel.channel_index}`}
                  </span>
                </CardTitle>
                {!ruleFormOpen && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setEditingRule(null); setRuleFormOpen(true); }}
                  >
                    + Add rule
                  </Button>
                )}
              </CardHeader>
              <CardContent className="pt-0 space-y-3">
                <SensorRuleList
                  rules={rules}
                  onEdit={(r) => { setEditingRule(r); setRuleFormOpen(true); }}
                  onDelete={handleDeleteRule}
                  onToggleEnabled={handleToggleRule}
                  onShowHistory={(r) => setHistoryRule(r)}
                />
                {ruleFormOpen && (
                  <SensorRuleForm
                    deviceId={device.device_id}
                    channel={activeChannel}
                    existing={editingRule || undefined}
                    onSaved={async () => {
                      setRuleFormOpen(false);
                      setEditingRule(null);
                      await reloadRules();
                    }}
                    onCancel={() => { setRuleFormOpen(false); setEditingRule(null); }}
                  />
                )}
              </CardContent>
            </Card>
          )}

          {/* Directly under the rules, because it is the second half of the
              same sentence: those rules fired, and this is what the site did
              about it. Renders nothing for a channel whose rules only notify. */}
          {selectedChannel && activeChannel && (
            <SensorRuleRelayActivity rules={rules} />
          )}

          {historyRule && (
            <SensorRuleEventsModal
              rule={historyRule}
              onClose={() => setHistoryRule(null)}
            />
          )}

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Settings</CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-3">
              <label className="block">
                <span className="text-xs text-gray-600">Name</span>
                <input
                  type="text"
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </label>
              {farms.length > 0 && (
                <label className="block">
                  <span className="text-xs text-gray-600">Farm</span>
                  <div className="mt-1 flex items-center gap-2">
                    <select
                      value={farmIdDraft ?? ''}
                      onChange={(e) => handleFarmChange(e.target.value ? Number(e.target.value) : null)}
                      disabled={savingFarm}
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
                    >
                      <option value="">No farm assigned</option>
                      {farms.map((f) => (
                        <option key={f.id} value={f.id}>{f.name}</option>
                      ))}
                    </select>
                    {savingFarm && <RefreshCw className="w-4 h-4 animate-spin text-gray-400" />}
                  </div>
                  <span className="text-xs text-gray-400 mt-1 block">
                    Assign this sensor to a farm so it shows up on the irrigation overview map.
                  </span>
                </label>
              )}
              <label className="block">
                <span className="text-xs text-gray-600">Config (JSON)</span>
                <textarea
                  value={configDraft}
                  onChange={(e) => setConfigDraft(e.target.value)}
                  rows={6}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </label>
              <div>
                <Button onClick={handleSave} disabled={saving} size="sm">
                  <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Device info</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <dl className="grid grid-cols-2 gap-y-2 text-sm">
                <dt className="text-gray-500">Device ID</dt>
                <dd className="font-mono">{device.device_id}</dd>
                <dt className="text-gray-500">Type</dt>
                <dd>{device.type_display_name || device.type_key}</dd>
                <dt className="text-gray-500">Transport</dt>
                <dd>{device.transport}</dd>
                <dt className="text-gray-500">Firmware</dt>
                <dd>{device.firmware_version || '—'}</dd>
                <dt className="text-gray-500">Parent sensor</dt>
                <dd className="font-mono">{device.parent_sensor_id || '—'}</dd>
                <dt className="text-gray-500">ESP-NOW controller</dt>
                <dd className="font-mono">{device.field_controller_id || '—'}</dd>
                {isMultiChannel && (
                  <>
                    <dt className="text-gray-500">Channels</dt>
                    <dd>{channels.length} configured</dd>
                  </>
                )}
              </dl>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
